import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { Formik, FormikValues, useFormikContext } from 'formik';
import {
  Row,
  Col,
  Dialog,
  isMobile,
  isDesktop,
  InputField,
  DialogType,
  FlexLayout,
  ButtonTypes,
  SelectField,
  ButtonColors,
  StandardButton,
  RadioFieldGroup,
  ButtonStyleTypes,
  StandardButtonWithRef,
} from '@gv/triage-components';

import { api } from 'api';
import { Config } from 'config';
import { InternalReportField } from 'components';
import { useCorporateOptions } from 'hooks/refer';
import { useDetailsQuery } from 'store/api/hospitals';
import { getSelectedCallOutcome } from 'utils/helpers';
import { surveyModal } from 'components/cases/form/config';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { useDetailsQuery as useClientQuery } from 'store/api/clients';
import { selectCurrentTaskForm } from 'store/api/action-queue/selectors';
import {
  CustomCallOutcomeLabels,
  CustomShelterCallOutcomeLabels,
} from 'types/data';
import {
  setIsShowFeedback,
  setIsShowPlanForCVT,
} from 'store/slices/analytics-export';
import {
  CallOutcomeRefer,
  CallOutcomeAction,
} from 'components/cases/form/call-outcome';
import {
  useFormEntities,
  callOutcomeModel,
  CheckProtocolsInfo,
} from 'components/cases/form';
import {
  CallOutcomeType,
  CustomCallOutcome,
  CallOutcomeAppointment,
  CustomShelterCallOutcome,
} from 'types';

import * as Styles from './styles';
import { FormValues } from './types';
import { getInitialValues, getAppointmentNotBookedOptions } from './config';

export const SubmitStep = () => {
  const mobile = isMobile();
  const desktop = isDesktop();
  const dispatch = useAppDispatch();
  const [isAdminAlert, setAdminAlert] = useState(false);
  const { isShowFeedback, isShowPlanForCVT } = useSelector(
    (state: RootState) => state.analyticsExport
  );
  const { values, setValues, setFieldValue } = useFormikContext<FormikValues>();
  const { clientId, hospital, hospitalId } = useFormEntities();
  const { corporateGroupOptions, isCorporateGroupFetching } =
    useCorporateOptions(hospitalId);

  const hospitalQuery = useDetailsQuery(String(hospitalId), {
    skip: !hospitalId,
  });

  const showFeedback = async () => {
    const response =
      await api.customerFeedback.checkTypeFormHospital(hospitalId);
    dispatch(setIsShowFeedback(response.data?.data?.status));
  };

  const caseForm = useAppSelector(selectCurrentTaskForm);

  const { data: client } = useClientQuery(
    { hospitalId, id: clientId!, allowDeleted: true },
    {
      skip: !clientId,
    }
  );
  const { hideCallOutcomeTypes, blockedCallOutcomeTypes } = useMemo(
    () => ({
      hideCallOutcomeTypes: Config.helpers.getHideCallOutcomeTypes(hospitalId),
      blockedCallOutcomeTypes:
        Config.helpers.getBlockedCallOutcomeTypes(hospitalId),
    }),
    [hospitalId]
  );

  useEffect(() => {
    showFeedback();
    if (
      String(hospital?.id ?? 0) !==
      (process.env.REACT_APP_SHOW_EMERGENCY_ONLY ?? '1')
    ) {
      dispatch(setIsShowPlanForCVT(true));
    } else {
      setFieldValue(callOutcomeModel.type, CallOutcomeType.Emergency);
    }
    if (values?.survey?.feedback_email === '') {
      if (caseForm?.client === undefined) {
        setFieldValue(
          surveyModal.feedback_email,
          client?.emails && client?.emails[0]
        );
      } else {
        setFieldValue(surveyModal.feedback_email, caseForm?.client?.email);
      }
    }
  }, []);

  useEffect(() => {
    const callReason = values?.callReason;

    if (callReason) {
      const availableOutcomes =
        hospitalQuery?.data?.hospital_calloutcome?.call_outcome
          ?.filter((outcome) => outcome != null && outcome.outcome != null)
          ?.map((outcome) => outcome.outcome) ?? [];

      const reasonToOutcomeMap: Record<string, string> = {
        Emergency: CallOutcomeType.Emergency,
        emergency: CallOutcomeType.Emergency,
        Appointment: CallOutcomeType.Appointment,
        appointment: CallOutcomeType.Appointment,
        Administrative: CallOutcomeType.Administrative,
        administrative: CallOutcomeType.Administrative,
      };

      const preferredOutcome = reasonToOutcomeMap[callReason];

      let selectedOutcome: string | undefined;

      if (
        preferredOutcome &&
        availableOutcomes.includes(preferredOutcome as any)
      ) {
        selectedOutcome = preferredOutcome;
      } else if (availableOutcomes.includes('administrative' as any)) {
        selectedOutcome = CallOutcomeType.Administrative;
      } else {
        selectedOutcome = undefined;
      }

      if (selectedOutcome) {
        setFieldValue(callOutcomeModel.type, selectedOutcome);
      } else {
        setFieldValue(callOutcomeModel.type, '');
      }
    }
  }, [
    values?.callReason,
    hospitalQuery?.data?.hospital_calloutcome?.call_outcome,
  ]);

  // Clear appointment_decline_reason when call outcome is not Appointment Declined
  useEffect(() => {
    const selectedCallOutcome = getSelectedCallOutcome(values?.callOutcome);
    const isAppointmentDeclined =
      selectedCallOutcome?.mainType === CallOutcomeType.Appointment &&
      selectedCallOutcome?.subType === CallOutcomeAppointment.Declined;

    if (!isAppointmentDeclined && values?.appointment_decline_reason) {
      setFieldValue('appointment_decline_reason', null);
    }
  }, [values?.callOutcome, values?.appointment_decline_reason, setFieldValue]);

  const permissions = {
    referToEr: hospital?.speciality !== 'Emergency',
    referToPPH: hospital?.speciality !== 'Emergency',
    referToOnCall: hospital?.speciality !== 'Emergency',
    appointmentTab: hospital?.speciality !== 'Emergency',
  };

  const callOutcomeItems = useMemo(
    () =>
      hospitalQuery?.data?.hospital_calloutcome?.call_outcome
        ?.filter((outcome) => outcome != null && outcome.outcome != null)
        ?.map((outcome) => ({
          value: outcome.outcome,
          label:
            hospital?.type === 2
              ? (CustomShelterCallOutcomeLabels[
                  outcome.outcome as CustomShelterCallOutcome
                ] ??
                CustomCallOutcomeLabels[outcome.outcome as CustomCallOutcome])
              : CustomCallOutcomeLabels[outcome.outcome as CustomCallOutcome],
        })) ?? [],
    [hospitalQuery?.data?.hospital_calloutcome?.call_outcome, hospital?.type]
  );

  const location = useLocation();
  const isMasterRecordURL = location.pathname.includes('master-records');

  return (
    <div>
      <FlexLayout gap={24} flexDirection="column">
        <div>
          <RadioFieldGroup
            label=""
            name={callOutcomeModel.type}
            onChange={(event) => {
              setAdminAlert(false);
              if (event === CallOutcomeType.Administrative) {
                setAdminAlert(true);
              }
            }}
            items={
              isMasterRecordURL
                ? [
                    {
                      label: 'Appointment',
                      value: CallOutcomeType.Appointment,
                      hasAccess:
                        hospital &&
                        permissions?.appointmentTab &&
                        hospital.type !== 2 &&
                        isShowPlanForCVT,
                    },
                    {
                      label: 'Emergency',
                      value: CallOutcomeType.Emergency,
                      hasAccess: hospital && hospital.type !== 2,
                    },
                    {
                      label: 'Administrative',
                      value: CallOutcomeType.Administrative,
                      hasAccess:
                        hospital &&
                        hospital.type !== 2 &&
                        !blockedCallOutcomeTypes?.includes(
                          CallOutcomeType.Administrative
                        ) &&
                        isShowPlanForCVT,
                    },
                  ]
                : callOutcomeItems?.length > 0
                  ? callOutcomeItems
                  : [
                      {
                        label: 'Appointment',
                        value: CallOutcomeType.Appointment,
                        hasAccess:
                          hospital &&
                          permissions?.appointmentTab &&
                          hospital.type !== 2 &&
                          isShowPlanForCVT,
                      },
                      {
                        label: 'Emergency',
                        value: CallOutcomeType.Emergency,
                        hasAccess: hospital && hospital.type !== 2,
                      },
                      {
                        label: 'Administrative',
                        value: CallOutcomeType.Administrative,
                        hasAccess:
                          hospital &&
                          hospital.type !== 2 &&
                          !blockedCallOutcomeTypes?.includes(
                            CallOutcomeType.Administrative
                          ) &&
                          isShowPlanForCVT,
                      },
                      {
                        value: 'referTorDVM',
                        label: 'Refer to rDVM',
                        hasAccess:
                          hospital &&
                          (hospital.speciality === 'Emergency' ||
                            hospital.speciality === 'Emergency & Specialty') &&
                          !hideCallOutcomeTypes?.includes(
                            CallOutcomeType.ReferTorDVM
                          ) &&
                          hospital.type !== 2 &&
                          isShowPlanForCVT,
                      },
                      {
                        label: 'Behavior',
                        value: CallOutcomeType.Behavior,
                        hasAccess:
                          hospital && hospital.type === 2 && isShowPlanForCVT,
                      },
                      {
                        label: 'Shelter Clinic',
                        value: CallOutcomeType.ShelterClinic,
                        hasAccess:
                          hospital && hospital.type === 2 && isShowPlanForCVT,
                      },
                      {
                        label: 'Refer to General Practice',
                        value: CallOutcomeType.ReferToGeneralPractice,
                        hasAccess:
                          hospital && hospital.type === 2 && isShowPlanForCVT,
                      },
                      {
                        label: 'Refer to Emergency',
                        value: CallOutcomeType.ReferToEmergency,
                        hasAccess:
                          hospital && hospital.type === 2 && isShowPlanForCVT,
                      },
                      {
                        label: 'Shelter Admin',
                        value: CallOutcomeType.ShelterAdmin,
                        hasAccess:
                          hospital && hospital.type === 2 && isShowPlanForCVT,
                      },
                    ]
            }
          />
        </div>

        {isShowFeedback && (
          <div>
            <Row>
              <Col
                col={6}
                style={{
                  marginBottom: '0px',
                }}
              >
                <InputField
                  placeholder="Add email"
                  type="textarea-autosize"
                  label="Email for sending survey"
                  name={surveyModal.feedback_email}
                  value={values?.survey?.feedback_email}
                />
              </Col>
            </Row>
          </div>
        )}

        <Dialog
          confirmText="Close"
          title="Please Note"
          text={<p>You have selected Administrative as the call outcome.</p>}
          buttons={(close) => (
            <StandardButton fullWidth text="Close" onClick={close} />
          )}
          open={
            isAdminAlert &&
            Config?.hideEmergencyCallOutcome
              ?.split(',')
              ?.includes(values.hospital_id + '')
          }
        />
        <CheckProtocolsInfo hideNotes={true} hospitalType="other">
          {hospital?.type !== 2 && (
            <CallOutcomeAction
              hideNotes
              permissions={permissions}
              corporateOptions={corporateGroupOptions}
              isCorporateFetching={isCorporateGroupFetching}
            />
          )}
          {hospital?.type !== 2 && isShowPlanForCVT && (
            <CallOutcomeRefer
              hideNotes
              permissions={permissions}
              corporateOptions={corporateGroupOptions}
              isCorporateFetching={isCorporateGroupFetching}
            />
          )}
        </CheckProtocolsInfo>

        {isShowPlanForCVT && (
          <InputField
            name="cvt_plan"
            placeholder="Add note"
            type="textarea-autosize"
            label="Recommended Action / Notes"
          />
        )}

        {(() => {
          const selectedCallOutcome = getSelectedCallOutcome(
            values?.callOutcome
          );
          const isAppointmentDeclined =
            selectedCallOutcome?.mainType === CallOutcomeType.Appointment &&
            selectedCallOutcome?.subType === CallOutcomeAppointment.Declined;

          return isAppointmentDeclined ? (
            <SelectField
              onlyValue
              isSearchable
              placeholder="Select a reason"
              name="appointment_decline_reason"
              label="Reason Appointment Was Not Booked"
              options={getAppointmentNotBookedOptions()}
            />
          ) : null;
        })()}
      </FlexLayout>
      Please click submit if consultation is finished. If you have any technical
      issues please report using internal reporting found{' '}
      <Dialog
        title="Internal reporting"
        closeOnDocumentClick={false}
        width={desktop ? '760px' : '90vw'}
        trigger={
          <StandardButtonWithRef
            transparent
            padding="0"
            text="here."
            styleType={ButtonStyleTypes.Ghost}
          />
        }
      >
        {(close, _, Buttons) => (
          <Formik
            initialValues={getInitialValues(values)}
            onSubmit={(formValues: FormValues) => {
              setValues((prevValues) => ({ ...prevValues, ...formValues }));
              close();
            }}
          >
            {({ dirty, isSubmitting }) => (
              <Styles.Form>
                <InternalReportField />

                <Buttons type={DialogType.Plain}>
                  <StandardButton
                    text="Close"
                    onClick={close}
                    fullWidth={mobile}
                  />
                  <StandardButton
                    text="Confirm"
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                    colorType={ButtonColors.Secondary}
                  />
                </Buttons>
              </Styles.Form>
            )}
          </Formik>
        )}
      </Dialog>
    </div>
  );
};
